import { useState } from 'react'

import { CardType } from 'types/deck'
import { PRICE_ICON_NAMES, PRICE_COLORS, PRICE_SOURCE, PRICE_CODES, HUMAN_PRICE_NAMES } from 'types/active'

import Checkbox from 'components/formElements/Checkbox'
import BuyCardsLink from 'components/formElements/BuyCardsLink'
import PhatButton from 'components/formElements/PhatButton'
import Icon from 'components/elements/Icon'

import { getPriceStringFromFloat, getLowestCostPriceIdFromEnabledSources } from 'utils/Price'

import styles from './buyCardsFromMultipileVendors.module.scss'

type Props = {
  cards: CardType[]
  linkedFrom: string
}

const BuyFromMultipleVendors = ({ cards, linkedFrom }: Props) => {
  const [selectedPriceSources, setSelectedPriceSources] = useState<Record<number, boolean>>({
    [PRICE_SOURCE.CK]: true,
    [PRICE_SOURCE.TCG]: true,
    [PRICE_SOURCE.MP]: true,
    [PRICE_SOURCE.SCG]: true,
  })

  const enabledSources = Object.keys(selectedPriceSources)
    .map(priceId => Number(priceId))
    .filter(priceId => selectedPriceSources[priceId])

  const minPricesPerCard: Record<number, CardType[]> = Object.keys(selectedPriceSources).reduce(
    (acc, priceSource) => ({ ...acc, [Number(priceSource)]: [] }),
    {},
  )

  for (const card of cards) {
    const minimumPriceIdFromEnabledSources = getLowestCostPriceIdFromEnabledSources(card, enabledSources)

    if (!minPricesPerCard[minimumPriceIdFromEnabledSources]) continue
    minPricesPerCard[minimumPriceIdFromEnabledSources].push(card)
  }

  return (
    <div>
      <p>
        Select your preferred vendors below, and we'll generate links to wherever any printing is the least expensive.
      </p>
      <div className={styles.checkboxGrid}>
        <Checkbox
          className={styles.nowrap}
          key={`ck-checkbox`}
          label="Card Kingdom ($)"
          checked={selectedPriceSources[PRICE_SOURCE.CK]}
          onChange={() =>
            setSelectedPriceSources({
              ...selectedPriceSources,
              [PRICE_SOURCE.CK]: !selectedPriceSources[PRICE_SOURCE.CK],
            })
          }
        />
        <Checkbox
          className={styles.nowrap}
          key={`tcg-checkbox`}
          label="TCGplayer ($)"
          checked={selectedPriceSources[PRICE_SOURCE.TCG]}
          onChange={() =>
            setSelectedPriceSources({
              ...selectedPriceSources,
              [PRICE_SOURCE.TCG]: !selectedPriceSources[PRICE_SOURCE.TCG],
            })
          }
        />
        <Checkbox
          className={styles.nowrap}
          key={`scg-checkbox`}
          label="Star City Games ($)"
          checked={selectedPriceSources[PRICE_SOURCE.SCG]}
          onChange={() =>
            setSelectedPriceSources({
              ...selectedPriceSources,
              [PRICE_SOURCE.SCG]: !selectedPriceSources[PRICE_SOURCE.SCG],
            })
          }
        />
        <Checkbox
          className={styles.nowrap}
          key={`mp-checkbox`}
          label="Mana Pool ($)"
          checked={selectedPriceSources[PRICE_SOURCE.MP]}
          onChange={() =>
            setSelectedPriceSources({
              ...selectedPriceSources,
              [PRICE_SOURCE.MP]: !selectedPriceSources[PRICE_SOURCE.MP],
            })
          }
        />
      </div>
      <div className={styles.buttonGrid}>
        {Object.keys(selectedPriceSources).map(priceIdKey => {
          const priceId = Number(priceIdKey)

          const priceCode = PRICE_CODES[priceId]
          const priceKey = `${priceCode}Minimum` // Eg: ckMinimum, tcgMinimum, etc
          const price = minPricesPerCard[priceId]?.reduce((acc, card) => (acc += card.prices[priceKey]), 0) || 0

          if (!selectedPriceSources[priceId]) return null

          return (
            <BuyCardsLink
              key={`${priceCode}-buylink`}
              priceSource={Number(priceId)}
              cards={minPricesPerCard[priceId] || []}
              linkedFrom={linkedFrom}
              isDisabled={!minPricesPerCard[priceId].length}>
              <PhatButton
                className={styles.retailerButton}
                disabled={!minPricesPerCard[priceId].length}
                style={{ backgroundColor: PRICE_COLORS[priceId] }}>
                <Icon className={PRICE_ICON_NAMES[priceId]} />
                {HUMAN_PRICE_NAMES[priceId]} {getPriceStringFromFloat(price, priceId)} (
                {minPricesPerCard[priceId]?.length || 0} / {cards.length} cards)
              </PhatButton>
            </BuyCardsLink>
          )
        })}
      </div>
    </div>
  )
}

export default BuyFromMultipleVendors
